import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/lib/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  pothdarGenerateConcepts, pothdarGetDesigns, pothdarScoreDesign, pothdarApproveDesign,
  pothdarPredictSales, pothdarPrepManufacturing, pothdarGenerate3D, pothdarGenMarketing,
  pothdarGetComponents, pothdarCreateComponent, pothdarGetBrandDNA, pothdarGetJobs, pothdarGetJob,
  pothdarChat, pothdarGenerateImage, pothdarGenerateVideo, pothdarGetMedia, pothdarDeleteMedia,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, CheckCircle, XCircle, Clock, Gem, TrendingUp, Factory, Image,
  Layers, Brain, ChevronRight, Star, Hammer, Palette, BarChart3, Package, ArrowLeft,
  MessageCircle, Send, Video, Play, ImagePlus, Wand2, Bot, User, Trash2, Copy, ExternalLink
} from 'lucide-react';

const PRODUCT_TYPES = ['ring', 'chain', 'pendant', 'coin', 'bar', 'bracelet', 'earring', 'necklace', 'bangle', 'anklet', 'nose-pin', 'mangalsutra', 'kada', 'toe-ring'];
const MATERIALS = ['gold', 'silver'];
const STYLES = ['minimal', 'spiritual', 'collectible', 'unisex', 'gen-z-luxury'];
const COMPONENT_TYPES = ['chain_link', 'pendant_base', 'ring_band', 'texture', 'symbol', 'stone', 'clasp', 'bail', 'prong', 'bezel'];
const IMAGE_STYLES = ['product_photography', 'lifestyle', 'flat_lay', 'dark_mood', 'editorial'];

const STATUS_ICON = { queued: Clock, processing: Loader2, completed: CheckCircle, failed: XCircle };
const STATUS_COLOR = { queued: 'text-yellow-400', processing: 'text-blue-400 animate-spin', completed: 'text-green-400', failed: 'text-red-400' };

export default function PothdarPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('chat');
  const [designs, setDesigns] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [components, setComponents] = useState([]);
  const [brandDNA, setBrandDNA] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState({});

  // Chat state
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', content: "Hey! I'm Pothdar, your AI jewelry design assistant. I can help you:\n\n• Generate jewelry design concepts\n• Create product images with Nano Banana\n• Make promotional videos with Sora 2\n• Write marketing copy\n• Analyze trends and provide manufacturing specs\n\nWhat would you like to create today?" }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const chatEndRef = useRef(null);

  // Image generation state
  const [imagePrompt, setImagePrompt] = useState('');
  const [imageStyle, setImageStyle] = useState('product_photography');
  
  // Video generation state
  const [videoPrompt, setVideoPrompt] = useState('');
  const [videoDuration, setVideoDuration] = useState(4);
  const [videoSize, setVideoSize] = useState('1280x720');

  // Concept form
  const [form, setForm] = useState({
    product_type: 'ring', material: 'gold', style: 'minimal',
    inspiration: '', features: '', target_price: '',
  });

  // Score modal
  const [scoreModal, setScoreModal] = useState({ open: false, design_id: '' });
  const [scores, setScores] = useState({ aesthetics_score: 8, brand_alignment_score: 8, uniqueness_score: 7, manufacturability_score: 8, comments: '' });

  // Component form
  const [compForm, setCompForm] = useState({ component_type: 'ring_band', style_tag: '', material_compatibility: 'gold,silver', description: '' });

  // Job detail
  const [jobDetail, setJobDetail] = useState(null);

  useEffect(() => {
    if (!user?.is_admin) return;
    loadJobs();
  }, [user]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const loadDesigns = async () => {
    try { const { data } = await pothdarGetDesigns(); setDesigns(data); } catch {}
  };
  const loadJobs = async () => {
    try { const { data } = await pothdarGetJobs(); setJobs(data); } catch {}
  };
  const loadComponents = async () => {
    try { const { data } = await pothdarGetComponents(); setComponents(data); } catch {}
  };
  const loadBrandDNA = async () => {
    try { const { data } = await pothdarGetBrandDNA(); setBrandDNA(data); } catch {}
  };
  const loadMedia = async () => {
    try { const { data } = await pothdarGetMedia(); setMedia(data); } catch {}
  };

  const setL = (key, val) => setLoading(p => ({ ...p, [key]: val }));

  // ─── Chat with Pothdar ─────────────────────────────────────
  const handleChat = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const userMsg = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setChatLoading(true);

    try {
      const { data } = await pothdarChat(userMsg, sessionId);
      if (data.session_id && !sessionId) setSessionId(data.session_id);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.response,
        suggestions: data.suggestions 
      }]);
    } catch (e) {
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.',
        error: true
      }]);
    }
    setChatLoading(false);
  };

  // ─── Generate Image ─────────────────────────────────────────
  const handleGenerateImage = async () => {
    if (!imagePrompt.trim()) return toast.error('Please enter an image prompt');
    setL('image', true);
    try {
      const { data } = await pothdarGenerateImage(imagePrompt, imageStyle);
      toast.success(`Image generation queued: ${data.job_id}`);
      setImagePrompt('');
      loadJobs();
      setTab('jobs');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to queue image generation'); }
    setL('image', false);
  };

  // ─── Generate Video ─────────────────────────────────────────
  const handleGenerateVideo = async () => {
    if (!videoPrompt.trim()) return toast.error('Please enter a video prompt');
    setL('video', true);
    try {
      const { data } = await pothdarGenerateVideo(videoPrompt, videoDuration, videoSize);
      toast.success(`Video generation queued: ${data.job_id} (may take 2-5 minutes)`);
      setVideoPrompt('');
      loadJobs();
      setTab('jobs');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to queue video generation'); }
    setL('video', false);
  };

  // ─── Generate Concepts ─────────────────────────────────────
  const handleGenerate = async () => {
    setL('generate', true);
    try {
      const payload = {
        ...form,
        features: form.features ? form.features.split(',').map(f => f.trim()) : [],
        target_price: form.target_price ? parseFloat(form.target_price) : null,
      };
      const { data } = await pothdarGenerateConcepts(payload);
      toast.success(`Job queued: ${data.job_id}`);
      loadJobs();
      setTab('jobs');
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    setL('generate', false);
  };

  // ─── Design Actions ────────────────────────────────────────
  const handleApprove = async (id) => {
    setL(id, true);
    try {
      await pothdarApproveDesign(id);
      toast.success('Design approved & added to brand memory');
      loadDesigns();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setL(id, false);
  };

  const handleScoreSubmit = async () => {
    try {
      await pothdarScoreDesign({ design_id: scoreModal.design_id, ...scores });
      toast.success('Score saved');
      setScoreModal({ open: false, design_id: '' });
      loadDesigns();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handlePredict = async (id) => {
    setL(`pred_${id}`, true);
    try {
      const { data } = await pothdarPredictSales(id);
      toast.success(`Sales prediction queued: ${data.job_id}`);
      loadJobs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setL(`pred_${id}`, false);
  };

  const handleManufacture = async (id) => {
    setL(`mfg_${id}`, true);
    try {
      const { data } = await pothdarPrepManufacturing(id);
      toast.success(`Manufacturing prep queued: ${data.job_id}`);
      loadJobs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setL(`mfg_${id}`, false);
  };

  const handleMarketing = async (id) => {
    setL(`mkt_${id}`, true);
    try {
      const { data } = await pothdarGenMarketing(id);
      toast.success(`Marketing generation queued: ${data.job_id}`);
      loadJobs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setL(`mkt_${id}`, false);
  };

  const handleCreateComponent = async () => {
    try {
      await pothdarCreateComponent({
        ...compForm,
        material_compatibility: compForm.material_compatibility.split(',').map(m => m.trim()),
      });
      toast.success('Component created');
      setCompForm({ component_type: 'ring_band', style_tag: '', material_compatibility: 'gold,silver', description: '' });
      loadComponents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const viewJob = async (jobId) => {
    try {
      const { data } = await pothdarGetJob(jobId);
      setJobDetail(data);
    } catch { toast.error('Could not load job'); }
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Admin access required</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="pothdar-dashboard">
      {/* Header */}
      <div className="border-b border-border/30 bg-background/95 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-5 h-5" /></button>
            <Brain className="w-6 h-6 text-gold" />
            <h1 className="font-display text-xl font-bold tracking-tight">Pothdar<span className="text-gold"> AI</span></h1>
            <Badge variant="outline" className="text-[10px] border-gold/30 text-gold">INTERNAL</Badge>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{designs.length} designs</span>
            <Separator orientation="vertical" className="h-4" />
            <span>{jobs.filter(j => j.status === 'processing').length} active jobs</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 py-6">
        <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === 'components') loadComponents(); if (v === 'memory') loadBrandDNA(); if (v === 'jobs') loadJobs(); if (v === 'designs') loadDesigns(); if (v === 'media') loadMedia(); }}>
          <TabsList className="bg-secondary/40 mb-6 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="chat" className="text-xs"><MessageCircle className="w-3.5 h-3.5 mr-1.5" />Chat</TabsTrigger>
            <TabsTrigger value="create" className="text-xs"><Wand2 className="w-3.5 h-3.5 mr-1.5" />Create</TabsTrigger>
            <TabsTrigger value="designs" className="text-xs"><Gem className="w-3.5 h-3.5 mr-1.5" />Designs</TabsTrigger>
            <TabsTrigger value="jobs" className="text-xs"><Clock className="w-3.5 h-3.5 mr-1.5" />Jobs</TabsTrigger>
            <TabsTrigger value="media" className="text-xs"><Image className="w-3.5 h-3.5 mr-1.5" />Media</TabsTrigger>
            <TabsTrigger value="components" className="text-xs"><Layers className="w-3.5 h-3.5 mr-1.5" />Components</TabsTrigger>
            <TabsTrigger value="memory" className="text-xs"><Brain className="w-3.5 h-3.5 mr-1.5" />Brand DNA</TabsTrigger>
          </TabsList>

          {/* ─── CHAT TAB ──────────────────────────────────── */}
          <TabsContent value="chat">
            <div className="max-w-3xl mx-auto">
              <div className="bg-card border border-border/40 rounded-xl overflow-hidden">
                {/* Chat messages */}
                <div className="h-[500px] overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
                  <AnimatePresence>
                    {chatMessages.map((msg, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.role === 'user' ? 'bg-gold/20' : 'bg-purple-500/20'}`}>
                          {msg.role === 'user' ? <User className="w-4 h-4 text-gold" /> : <Bot className="w-4 h-4 text-purple-400" />}
                        </div>
                        <div className={`max-w-[80%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                          <div className={`rounded-2xl px-4 py-3 ${msg.role === 'user' ? 'bg-gold/10 text-foreground' : msg.error ? 'bg-red-500/10 text-red-400' : 'bg-secondary/50 text-foreground'}`}>
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          </div>
                          {msg.suggestions?.length > 0 && (
                            <div className="flex gap-2 mt-2">
                              {msg.suggestions.map((s, j) => (
                                <Badge key={j} variant="outline" className="text-[10px] cursor-pointer hover:bg-gold/10" onClick={() => setTab('create')}>
                                  {s.type === 'image' ? <ImagePlus className="w-3 h-3 mr-1" /> : <Video className="w-3 h-3 mr-1" />}
                                  {s.message}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  {chatLoading && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-purple-400" />
                      </div>
                      <div className="bg-secondary/50 rounded-2xl px-4 py-3">
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
                
                {/* Chat input */}
                <div className="border-t border-border/30 p-4">
                  <div className="flex gap-2">
                    <Input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleChat()}
                      placeholder="Ask Pothdar anything about jewelry design..."
                      className="bg-secondary/40 border-0"
                      data-testid="chat-input"
                    />
                    <Button onClick={handleChat} disabled={chatLoading || !chatInput.trim()} className="bg-gold hover:bg-gold/90 text-background" data-testid="chat-send">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">Powered by Claude Sonnet 4.5 • Ask about designs, trends, marketing, or manufacturing</p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ─── CREATE TAB ────────────────────────────────── */}
          <TabsContent value="create">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Image Generation */}
              <div className="bg-card border border-border/40 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <ImagePlus className="w-5 h-5 text-gold" />
                  <h3 className="font-display font-bold">Generate Image</h3>
                  <Badge className="text-[9px] bg-purple-500/20 text-purple-400">Nano Banana</Badge>
                </div>
                <div className="space-y-3">
                  <Textarea
                    value={imagePrompt}
                    onChange={(e) => setImagePrompt(e.target.value)}
                    placeholder="Describe the jewelry image you want... e.g., 'A minimalist gold ring with sacred geometry patterns, studio lighting'"
                    className="bg-secondary/40 border-0 min-h-[100px] text-sm"
                    data-testid="image-prompt"
                  />
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Style</label>
                    <Select value={imageStyle} onValueChange={setImageStyle}>
                      <SelectTrigger className="bg-secondary/40 border-border h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {IMAGE_STYLES.map(s => <SelectItem key={s} value={s} className="text-xs">{s.replace(/_/g, ' ')}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleGenerateImage} disabled={loading.image || !imagePrompt.trim()} className="w-full bg-gold hover:bg-gold/90 text-background" data-testid="generate-image-btn">
                    {loading.image ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ImagePlus className="w-4 h-4 mr-2" />}
                    Generate Image
                  </Button>
                </div>
              </div>

              {/* Video Generation */}
              <div className="bg-card border border-border/40 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Video className="w-5 h-5 text-gold" />
                  <h3 className="font-display font-bold">Generate Video</h3>
                  <Badge className="text-[9px] bg-blue-500/20 text-blue-400">Sora 2</Badge>
                </div>
                <div className="space-y-3">
                  <Textarea
                    value={videoPrompt}
                    onChange={(e) => setVideoPrompt(e.target.value)}
                    placeholder="Describe the promotional video... e.g., 'Slow motion shot of gold chain dropping onto velvet, sparkling under soft light'"
                    className="bg-secondary/40 border-0 min-h-[100px] text-sm"
                    data-testid="video-prompt"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Duration</label>
                      <Select value={String(videoDuration)} onValueChange={(v) => setVideoDuration(parseInt(v))}>
                        <SelectTrigger className="bg-secondary/40 border-border h-9 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="4" className="text-xs">4 seconds</SelectItem>
                          <SelectItem value="8" className="text-xs">8 seconds</SelectItem>
                          <SelectItem value="12" className="text-xs">12 seconds</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Size</label>
                      <Select value={videoSize} onValueChange={setVideoSize}>
                        <SelectTrigger className="bg-secondary/40 border-border h-9 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="1280x720" className="text-xs">HD (16:9)</SelectItem>
                          <SelectItem value="1024x1024" className="text-xs">Square</SelectItem>
                          <SelectItem value="1024x1792" className="text-xs">Vertical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={handleGenerateVideo} disabled={loading.video || !videoPrompt.trim()} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="generate-video-btn">
                    {loading.video ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    Generate Video (2-5 min)
                  </Button>
                </div>
              </div>

              {/* Concept Generation */}
              <div className="bg-card border border-border/40 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-gold" />
                  <h3 className="font-display font-bold">Design Concepts</h3>
                  <Badge className="text-[9px] bg-green-500/20 text-green-400">Claude</Badge>
                </div>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={form.product_type} onValueChange={v => setForm(p => ({ ...p, product_type: v }))}>
                      <SelectTrigger className="bg-secondary/40 border-border h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-card border-border max-h-60">
                        {PRODUCT_TYPES.map(t => <SelectItem key={t} value={t} className="text-xs capitalize">{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={form.material} onValueChange={v => setForm(p => ({ ...p, material: v }))}>
                      <SelectTrigger className="bg-secondary/40 border-border h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {MATERIALS.map(m => <SelectItem key={m} value={m} className="text-xs capitalize">{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input placeholder="Inspiration (e.g., sacred geometry)" value={form.inspiration} onChange={e => setForm(p => ({ ...p, inspiration: e.target.value }))} className="bg-secondary/40 text-xs h-9" />
                  <Input placeholder="Features (comma-separated)" value={form.features} onChange={e => setForm(p => ({ ...p, features: e.target.value }))} className="bg-secondary/40 text-xs h-9" />
                  <Button onClick={handleGenerate} disabled={loading.generate} className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="generate-concepts-btn">
                    {loading.generate ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Generate 7 Concepts
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ─── DESIGNS TAB ──────────────────────────────── */}
          <TabsContent value="designs">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-bold">{designs.length} Designs</h2>
              <Button variant="outline" size="sm" onClick={loadDesigns} className="text-xs">Refresh</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {designs.map((d) => (
                <motion.div key={d.design_id} className="bg-card border border-border/40 rounded-xl p-4 hover:border-gold/30 transition-colors" initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid={`design-card-${d.design_id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display font-bold text-sm truncate">{d.name}</h3>
                      <p className="text-[11px] text-muted-foreground font-mono">{d.design_id}</p>
                    </div>
                    <Badge className={`text-[10px] flex-shrink-0 ${d.approval_status === 'approved' ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30'}`}>
                      {d.approval_status}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{d.description}</p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    <Badge variant="outline" className="text-[10px] capitalize">{d.material}</Badge>
                    <Badge variant="outline" className="text-[10px] capitalize">{d.product_type}</Badge>
                    {d.style_tags?.slice(0, 2).map(t => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                  </div>
                  {d.estimated_weight_g && <p className="text-[11px] text-muted-foreground mb-1">{d.estimated_weight_g}g • Rs {d.estimated_price?.toLocaleString()}</p>}
                  <div className="flex flex-wrap gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 text-[11px] px-2" onClick={() => setScoreModal({ open: true, design_id: d.design_id })}>
                      <Star className="w-3 h-3 mr-1" />Score
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-[11px] px-2" onClick={() => handlePredict(d.design_id)} disabled={loading[`pred_${d.design_id}`]}>
                      {loading[`pred_${d.design_id}`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><TrendingUp className="w-3 h-3 mr-1" />Predict</>}
                    </Button>
                    {d.approval_status !== 'approved' && (
                      <Button size="sm" className="h-7 text-[11px] px-2 bg-green-600 hover:bg-green-700 text-white" onClick={() => handleApprove(d.design_id)} disabled={loading[d.design_id]}>
                        {loading[d.design_id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle className="w-3 h-3 mr-1" />Approve</>}
                      </Button>
                    )}
                    {d.approval_status === 'approved' && (
                      <>
                        <Button size="sm" variant="outline" className="h-7 text-[11px] px-2" onClick={() => handleManufacture(d.design_id)} disabled={loading[`mfg_${d.design_id}`]}>
                          {loading[`mfg_${d.design_id}`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Factory className="w-3 h-3 mr-1" />Mfg</>}
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-[11px] px-2" onClick={() => handleMarketing(d.design_id)} disabled={loading[`mkt_${d.design_id}`]}>
                          {loading[`mkt_${d.design_id}`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Image className="w-3 h-3 mr-1" />Marketing</>}
                        </Button>
                      </>
                    )}
                  </div>
                </motion.div>
              ))}
              {designs.length === 0 && <p className="text-muted-foreground col-span-3 text-center py-12">No designs yet. Generate some concepts first.</p>}
            </div>
          </TabsContent>

          {/* ─── JOBS TAB ─────────────────────────────────── */}
          <TabsContent value="jobs">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-bold">Job Queue</h2>
              <Button variant="outline" size="sm" onClick={loadJobs} className="text-xs">Refresh</Button>
            </div>
            <div className="space-y-2">
              {jobs.map((j) => {
                const Icon = STATUS_ICON[j.status] || Clock;
                return (
                  <div key={j.job_id} className="bg-card border border-border/40 rounded-lg p-3 flex items-center gap-3 cursor-pointer hover:border-gold/30 transition-colors" onClick={() => viewJob(j.job_id)} data-testid={`job-${j.job_id}`}>
                    <Icon className={`w-4 h-4 flex-shrink-0 ${STATUS_COLOR[j.status]}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono">{j.job_id}</span>
                        <Badge variant="outline" className="text-[10px]">{j.job_type.replace(/_/g, ' ')}</Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground">{new Date(j.created_at).toLocaleString('en-IN')}</p>
                    </div>
                    <Badge className={`text-[10px] ${j.status === 'completed' ? 'bg-green-500/20 text-green-400' : j.status === 'failed' ? 'bg-red-500/20 text-red-400' : j.status === 'processing' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/15 text-yellow-400'}`}>
                      {j.status}
                    </Badge>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                );
              })}
              {jobs.length === 0 && <p className="text-muted-foreground text-center py-12">No jobs yet.</p>}
            </div>
          </TabsContent>

          {/* ─── MEDIA TAB ─────────────────────────────────── */}
          <TabsContent value="media">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-bold">Generated Media ({media.length})</h2>
              <Button variant="outline" size="sm" onClick={loadMedia} className="text-xs">Refresh</Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {media.map((m) => {
                const mediaUrl = `${process.env.REACT_APP_BACKEND_URL}/api${m.path}`;
                return (
                  <div key={m.media_id} className="bg-card border border-border/40 rounded-xl overflow-hidden group relative" data-testid={`media-${m.media_id}`}>
                    {m.type === 'image' ? (
                      <div className="aspect-square bg-secondary/30 flex items-center justify-center relative">
                        <img 
                          src={mediaUrl} 
                          alt={m.prompt} 
                          className="w-full h-full object-cover" 
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} 
                        />
                        <div className="hidden items-center justify-center absolute inset-0 bg-secondary/50">
                          <Image className="w-8 h-8 text-muted-foreground" />
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-video bg-secondary/30 flex items-center justify-center relative">
                        <video 
                          src={mediaUrl} 
                          className="w-full h-full object-cover" 
                          controls 
                          poster=""
                        />
                      </div>
                    )}
                    {/* Hover Actions */}
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button 
                        size="sm" 
                        variant="secondary" 
                        className="h-7 w-7 p-0 bg-background/80 hover:bg-background"
                        onClick={() => {
                          navigator.clipboard.writeText(mediaUrl);
                          toast.success('URL copied to clipboard!');
                        }}
                        title="Copy URL"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="secondary" 
                        className="h-7 w-7 p-0 bg-background/80 hover:bg-background"
                        onClick={() => window.open(mediaUrl, '_blank')}
                        title="Open in new tab"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        className="h-7 w-7 p-0"
                        onClick={async () => {
                          if (window.confirm('Delete this media?')) {
                            try {
                              await pothdarDeleteMedia(m.media_id);
                              toast.success('Media deleted');
                              loadMedia();
                            } catch (e) {
                              toast.error('Failed to delete');
                            }
                          }
                        }}
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <div className="p-3">
                      <div className="flex items-center justify-between mb-1">
                        <Badge className={`text-[9px] ${m.type === 'image' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}`}>
                          {m.type === 'image' ? 'Nano Banana' : 'Sora 2'}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground">{new Date(m.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">{m.prompt}</p>
                    </div>
                  </div>
                );
              })}
              {media.length === 0 && <p className="text-muted-foreground col-span-4 text-center py-12">No media generated yet. Use the Create tab to generate images or videos.</p>}
            </div>
          </TabsContent>

          {/* ─── COMPONENTS TAB ───────────────────────────── */}
          <TabsContent value="components">
            <h2 className="font-display text-lg font-bold mb-4">Component Library</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1">
                <div className="bg-card border border-border/40 rounded-xl p-4 space-y-3">
                  <h3 className="text-sm font-bold mb-2">Add Component</h3>
                  <Select value={compForm.component_type} onValueChange={v => setCompForm(p => ({ ...p, component_type: v }))}>
                    <SelectTrigger className="bg-secondary/40 border-border h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {COMPONENT_TYPES.map(t => <SelectItem key={t} value={t} className="text-xs">{t.replace(/_/g, ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input placeholder="Style tag (e.g. minimal)" value={compForm.style_tag} onChange={e => setCompForm(p => ({ ...p, style_tag: e.target.value }))} className="bg-secondary/40 text-xs" />
                  <Input placeholder="Materials (e.g. gold,silver)" value={compForm.material_compatibility} onChange={e => setCompForm(p => ({ ...p, material_compatibility: e.target.value }))} className="bg-secondary/40 text-xs" />
                  <Input placeholder="Description" value={compForm.description} onChange={e => setCompForm(p => ({ ...p, description: e.target.value }))} className="bg-secondary/40 text-xs" />
                  <Button size="sm" onClick={handleCreateComponent} className="w-full rounded-full bg-gold text-background hover:bg-gold/90 text-xs">
                    <Layers className="w-3.5 h-3.5 mr-1" />Add Component
                  </Button>
                </div>
              </div>
              <div className="lg:col-span-2 space-y-2">
                {components.map(c => (
                  <div key={c.component_id} className="bg-card border border-border/40 rounded-lg p-3 flex items-center gap-3">
                    <Palette className="w-4 h-4 text-gold flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{c.component_type.replace(/_/g, ' ')}</Badge>
                        <Badge variant="outline" className="text-[10px]">{c.style_tag}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{c.description}</p>
                    </div>
                  </div>
                ))}
                {components.length === 0 && <p className="text-muted-foreground text-center py-8">No components yet.</p>}
              </div>
            </div>
          </TabsContent>

          {/* ─── BRAND DNA TAB ────────────────────────────── */}
          <TabsContent value="memory">
            <h2 className="font-display text-lg font-bold mb-4">Brand DNA</h2>
            {brandDNA ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-card border border-border/40 rounded-xl p-5">
                  <BarChart3 className="w-5 h-5 text-gold mb-3" />
                  <p className="font-display text-3xl font-bold">{brandDNA.total_approved}</p>
                  <p className="text-xs text-muted-foreground uppercase tracking-widest">Approved Designs</p>
                </div>
                <div className="bg-card border border-border/40 rounded-xl p-5">
                  <Gem className="w-5 h-5 text-gold mb-3" />
                  <p className="text-sm font-bold mb-2">Material Split</p>
                  {Object.entries(brandDNA.material_distribution).map(([m, c]) => (
                    <div key={m} className="flex justify-between text-xs"><span className="capitalize text-muted-foreground">{m}</span><span className="font-mono">{c}</span></div>
                  ))}
                </div>
                <div className="bg-card border border-border/40 rounded-xl p-5">
                  <Package className="w-5 h-5 text-gold mb-3" />
                  <p className="text-sm font-bold mb-2">Product Types</p>
                  {Object.entries(brandDNA.product_type_distribution).map(([t, c]) => (
                    <div key={t} className="flex justify-between text-xs"><span className="capitalize text-muted-foreground">{t}</span><span className="font-mono">{c}</span></div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-gold animate-spin" /></div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ─── Score Modal ────────────────────────────────────── */}
      <Dialog open={scoreModal.open} onOpenChange={o => !o && setScoreModal({ open: false, design_id: '' })}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle className="font-display">Score Design</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground font-mono mb-2">{scoreModal.design_id}</p>
          <div className="space-y-3">
            {[
              { key: 'aesthetics_score', label: 'Aesthetics' },
              { key: 'brand_alignment_score', label: 'Brand Alignment' },
              { key: 'uniqueness_score', label: 'Uniqueness' },
              { key: 'manufacturability_score', label: 'Manufacturability' },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between">
                <label className="text-xs text-muted-foreground">{label}</label>
                <Input type="number" min={0} max={10} step={0.5} value={scores[key]} onChange={e => setScores(p => ({ ...p, [key]: parseFloat(e.target.value) || 0 }))} className="w-20 bg-secondary/40 text-center text-xs h-8" />
              </div>
            ))}
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Comments</label>
              <Input placeholder="Optional notes" value={scores.comments} onChange={e => setScores(p => ({ ...p, comments: e.target.value }))} className="bg-secondary/40 text-xs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setScoreModal({ open: false, design_id: '' })}>Cancel</Button>
            <Button onClick={handleScoreSubmit} className="bg-gold text-background">Save Score</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Job Detail Modal ───────────────────────────────── */}
      <Dialog open={!!jobDetail} onOpenChange={o => !o && setJobDetail(null)}>
        <DialogContent className="bg-background border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Job Detail</DialogTitle></DialogHeader>
          {jobDetail && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="text-xs font-mono">{jobDetail.job_id}</Badge>
                <Badge variant="outline" className="text-xs">{jobDetail.job_type.replace(/_/g, ' ')}</Badge>
                <Badge className={`text-xs ${jobDetail.status === 'completed' ? 'bg-green-500/20 text-green-400' : jobDetail.status === 'failed' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'}`}>{jobDetail.status}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">{new Date(jobDetail.created_at).toLocaleString('en-IN')}</p>
              {jobDetail.error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-xs text-red-400">{jobDetail.error}</div>}
              {jobDetail.result_data && (
                <div className="bg-secondary/20 rounded-lg p-3">
                  <p className="text-xs font-bold mb-2">Result</p>
                  <pre className="text-[11px] text-muted-foreground whitespace-pre-wrap overflow-x-auto max-h-96">
                    {JSON.stringify(jobDetail.result_data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
